import React, {useState} from 'react';
import {Box, Button, Grid, Typography} from "@mui/material";
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import {useLocation, useNavigate} from "react-router-dom";
import Top from "../Top";
import Bottom from "../Bottom";
import HistoryDeviceBloodMonthHistory from "./HistoryDeviceBloodMonthHistory";
import HistoryDevicePressureMonthHistory from "./HistoryDevicePressureMonthHistory";
import GaugeChart from "../../components/GaugeChart";
import RadialChart from "../../components/RadialChart";
import LineChart from "../../components/LineChart";
import DeviceNormalRange from "../../components/DeviceNormalRange";
import * as util from "../../utils/util";
import {
  getDeviceById,
  getGaugeValue,
  getPrimaryMetric
} from "../../utils/deviceInfo";
import { monthDailyOf, monthOf, useHealthDevice } from "../../utils/healthDeviceApi";

const HistoryDeviceMonthHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [monthValue, setMonthValue] = useState(new Date().getMonth() + 1);
  const deviceId = (location.state || {}).deviceId || '';
  const device = getDeviceById(deviceId);
  // 훅은 조기 반환보다 먼저 호출한다
  const { data: deviceData } = useHealthDevice(0);

  // 선택한 기기 정보가 없으면 기기 선택 화면으로 되돌림
  if (device === undefined) {
    return (
      <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
        <Top title="월간 기기 기록" />
        <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
          <Typography sx={{ fontSize: 16, color: '#555', textAlign: 'center', mt: 5 }}>기기 정보를 찾을 수 없습니다.</Typography>
          <Stack alignItems={'center'} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => navigate('../history/HistoryDevice')} sx={DeviceChangeBtnStyle}>기기 선택하기</Button>
          </Stack>
        </Grid>
        <Bottom />
      </Stack>
    );
  }

  // 혈당기는 기존 월간 혈당 화면과 동일한 시간대별 구성 사용
  if (device.type === 'blood') {
    return <HistoryDeviceBloodMonthHistory device={device} />;
  }

  // 혈압계는 기존 월간 혈압 화면과 동일한 수축기 / 이완기 구성 사용
  if (device.type === 'pressure') {
    return <HistoryDevicePressureMonthHistory device={device} />;
  }

  const metric = getPrimaryMetric(device.id);
  // AP_009_004 의 일자별 요약에서 이번 달 값을 계산한다
  const monthInfo = monthOf(deviceData, device, 0);
  const monthDaily = monthDailyOf(deviceData, device, 0);

  const monthSeries = [{ curve: 'linear', data: monthDaily }];
  const xAxis = [{ data: monthDaily.map((_, index) => index + 1) }];

  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title={'월간 ' + device.typeName + ' 기록'} />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%' }}>
        <Box>

          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setMonthValue((prev) => prev - 1)} sx={{ 'display': monthValue > 1 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
            {new Date().getFullYear() + '년 ' + monthValue + '월'}
            <ArrowRightIcon onClick={() => setMonthValue((prev) => prev + 1)} sx={{ 'display': monthValue < new Date().getMonth() + 1 && monthValue > 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          <Stack direction={'column'} justifyContent={'center'} alignItems={'center'} sx={{ m: '20px 0', p: 1, bgcolor: '#fff', borderRadius: '15px', boxShadow: CardShadow }}>
            <Typography sx={{ fontSize: 14, color: '#333', fontWeight: 700, mb: 1 }}>{device.name}</Typography>

            {/* 체중계는 월간 체중 기록 화면과 동일한 방사형 게이지 사용 */}
            {device.type === 'weight' ? (
              <Box sx={{ '& svg': { width: '220px !important', height: '160px !important' }, mb: 2 }}>
                <RadialChart value={monthInfo.valueAvg} />
              </Box>
            ) : (
              <Box sx={{ '& svg': { width: '220px !important', height: '130px !important' }, mb: 2 }}>
                <GaugeChart targetValue={getGaugeValue(metric, monthInfo.valueAvg)} />
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              {
                monthInfo.recordCount > 0 ? (
                  monthInfo.recordPercent === 100 ? (
                    <Typography sx={{ fontSize: 14, color: '#ef8322', fontWeight: 700 }}>{device.typeName} 측정을 잘 하고 있어요</Typography>
                  ) : (
                    <Typography sx={{ fontSize: 14, color: '#555', fontWeight: 700 }}>{device.typeName} 측정이 완벽히 되지 않았어요</Typography>
                  )
                ) : (
                  <Typography sx={{ fontSize: 14, color: '#777', fontWeight: 700 }}>아직 기록이 없어요</Typography>
                )
              }
            </Box>

            <Stack direction={'row'} justifyContent={'space-evenly'} alignItems={'center'} sx={{ width: '100%', mb: 1 }}>
              <Stack direction={'column'} justifyContent={'center'} alignItems={'center'}>
                <Typography sx={{ fontSize: 14, color: '#555' }}>일일 평균 {metric.label}</Typography>
                <Typography component="div" sx={{ fontSize: 20, color: '#777', fontWeight: 700 }}>
                  {monthInfo.valueAvg}
                  <Typography component="span" sx={{ fontSize: 14, color: '#777', ml: 1 }}>{metric.unit}</Typography>
                </Typography>
              </Stack>
              <Divider orientation="vertical" variant="middle" flexItem />
              <Stack direction={'column'} justifyContent={'center'} alignItems={'center'}>
                <Typography sx={{ fontSize: 14, color: '#555' }}>측정 완료일</Typography>
                <Typography component="div" sx={{ fontSize: 20, color: '#777', fontWeight: 700 }}>
                  {util.formatComma(monthInfo.recordCount)}
                  <Typography component="span" sx={{ fontSize: 14, color: '#777', ml: 1 }}>일</Typography>
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          <Box sx={{ bgcolor: '#fff', p: '10px', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack>
                <Typography component="div" sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>월간현황</Typography>
              </Stack>
            </Stack>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography component="div" sx={{ fontSize: 12, color: '#555', mr: .5 }}>이번 달의
                <Typography component="span" sx={{ fontSize: 14, color: '#0069ce', fontWeight: 700, mb: 2 }}> {metric.label} </Typography>
                기록이에요.
              </Typography>
            </Box>
            <Divider />

            <Box sx={{ mt: -3.5, '& .MuiChartsAxis-tickContainer:last-child': { display: 'none' } }}>
              {/* 체중계는 월간 체중 기록 화면과 동일하게 정상 체중 기준선 노출 */}
              <LineChart series={monthSeries} xAxis={xAxis} isReferenceLine={device.type === 'weight'} />
            </Box>
          </Box>

          <DeviceNormalRange deviceType={device.type} />
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

const CardShadow = '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)';

const DeviceChangeBtnStyle = {
  width: '100%',
  bgcolor: '#fff',
  fontSize: 16,
  fontWeight: 700,
  borderRadius: '30px', p: 1,
  boxSizing: 'border-box',
  color: '#EF8322',
  border: '1px solid #EF8322',
  ':hover': {
    backgroundColor: '#fff',
    borderColor: '#EF8322',
    boxShadow: 'none',
  },
};

export default HistoryDeviceMonthHistory;
